import type { CustomPin, CustomSymbol, Prim } from './custom';
import type { PdfPageData, PdfText } from './pdfExtract';
import { newId } from './tagging';
import { rotP } from './geometry';
import type { Pt, Sym, Wire } from './types';

/*
 * Devices of an EPLAN schematic PDF. Symbol graphics are the paths in the
 * colour that meets the free wire ends most; touching graphics form one
 * device, the free wire ends on it become its connection points (exactly
 * where the wires end), labelled by the small text next to each. The device
 * keeps its EPLAN drawing as a user symbol stored in the project; identical
 * drawings share one symbol. The tag and description come from the texts
 * beside it, the role from the connection point names: A1/A2 coil, 13/14 or
 * 21/22 contact of the device with the same tag, a small circle a terminal.
 */

type Seg = [Pt, Pt];
interface Piece { segs: Seg[]; bb: [number, number, number, number]; dash: boolean; blk?: string }
export interface PlacedText { t: PdfText; c: Pt }

export interface DeviceContext {
  pg: PdfPageData;
  /** page mm → EnerCAD mm (without snapping) */
  T: (p: Pt) => Pt;
  /** scale of T */
  k: number;
  wireColour: string;
  symbolColours: string[];
  wires: Wire[];
  syms: Sym[];
  texts: PlacedText[];
  used: Set<PdfText>;
  /** user symbols of the whole import, by signature */
  library: Map<string, CustomSymbol>;
  /** the drawing area of the page (texts outside it belong to the frame) */
  area: [number, number, number, number];
  /** strokes (page → EnerCAD mm, unsnapped) that are wires, not drawing (one-colour drawings) */
  skip?: (a: Pt, b: Pt) => boolean;
  /** a one-colour drawing (older AutoCAD style) */
  mono?: boolean;
}

export interface DeviceStats { devices: number; contacts: number; terminals: number; untagged: number; texts: number }

/** Gap still bridged inside one symbol: open contacts leave about 2 mm between blade and terminal. */
const JOIN = 2.2;
const r2 = (v: number) => Math.round(v * 100) / 100;
const snap = (v: number) => Math.round(v * 2) / 2;
const XREF = /^(\/\s*)?\d+\.\d+$|^\/\s*\d+(\.\d+)?$|^\d+\.\d+\s*\/$|^\/$/;
/** Device tags: "F1.1", "-1T1", "EG1", "K10:2"; no spaces, upper case and digits. */
const TAGLIKE = /^-?[A-ZÇĞİÖŞÜ0-9][A-ZÇĞİÖŞÜ0-9.\-:/]*$/;
/** "1F1.1 1P6A": a tag with its description in one text (older drawings); the tag and the rest. */
const splitTag = (s: string): [string, string] => {
  const m = /^(\S+)\s+(.+)$/.exec(s.trim());
  return m && TAGLIKE.test(m[1]) && /[A-ZÇĞİÖŞÜ]/.test(m[1]) && /\d/.test(m[1]) ? [m[1], m[2]] : [s, ''];
};

function segDist(p: Pt, [a, b]: Seg): number {
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}
const bbOf = (pts: Pt[]): [number, number, number, number] => [
  Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1])),
];
const bbDist = (p: Pt, b: number[]) => Math.hypot(Math.max(b[0] - p[0], 0, p[0] - b[2]), Math.max(b[1] - p[1], 0, p[1] - b[3]));
/** A circle drawn as one line: many short strokes in a square box (width in mm). */
const ringOf = (p: Piece) => (p.segs.length >= 8 && Math.abs(p.bb[2] - p.bb[0] - (p.bb[3] - p.bb[1])) < 0.3 ? p.bb[2] - p.bb[0] : 0);

/**
 * A terminal strip drawn as a stack of cells (a circle in a square each, the squares touching) is one terminal per
 * cell: every stroke goes with the nearest circle. Other groups stay whole.
 */
function cells(ps: Piece[]): Piece[][] {
  const rings = ps.filter((p) => ringOf(p) >= 1.5 && ringOf(p) <= 8);
  if (rings.length < 2) return [ps];
  const w = ringOf(rings[0]);
  if (rings.some((r) => Math.abs(ringOf(r) - w) > 0.3)) return [ps];
  const mid = (b: number[]): Pt => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
  const centres = rings.map((r) => mid(r.bb));
  // only straight strokes within a cell's reach of a circle
  const reach = w * 0.75;
  for (const p of ps) {
    if (rings.includes(p)) continue;
    if (p.segs.length > 4 || !centres.some((c) => bbDist(c, p.bb) <= reach)) return [ps];
  }
  const out: Piece[][] = rings.map((r) => [r]);
  for (const p of ps) {
    if (rings.includes(p)) continue;
    const m = mid(p.bb);
    let best = 0;
    centres.forEach((c, i) => {
      if (Math.hypot(c[0] - m[0], c[1] - m[1]) < Math.hypot(centres[best][0] - m[0], centres[best][1] - m[1])) best = i;
    });
    out[best].push(p);
  }
  return out;
}
const bbOfPieces = (ps: Piece[]) => bbOf(ps.flatMap((p) => [[p.bb[0], p.bb[1]], [p.bb[2], p.bb[3]]] as Pt[]));

/**
 * Older drawings leave a gap between the circles of a changeover contact (14 ○ ○ 12 side by side, the blade from
 * 11 to one of them): small groups whose terminal circles stand in line, up to 5 mm of the drawing apart, are one
 * contact.
 */
function changeovers(groups: Piece[][], k: number): Piece[][] {
  const mid = (b: number[]): Pt => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
  const small = (ps: Piece[]) => {
    const b = bbOfPieces(ps);
    return b[2] - b[0] <= 25 * k && b[3] - b[1] <= 25 * k;
  };
  const rings = groups.map((ps) => (small(ps) ? ps.filter((p) => ringOf(p) > 0 && ringOf(p) <= 2.6).map((p) => mid(p.bb)) : []));
  const parent = groups.map((_, i) => i);
  const find = (i: number): number => {
    // iterative with path compression: a long chain in a big drawing must not overflow the stack
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) [parent[i], i] = [r, parent[i]];
    return r;
  };
  for (let i = 0; i < groups.length; i++)
    for (let j = i + 1; j < groups.length; j++)
      if (rings[i].some((a) => rings[j].some((b) => (Math.abs(a[1] - b[1]) < 0.3 || Math.abs(a[0] - b[0]) < 0.3) && Math.hypot(a[0] - b[0], a[1] - b[1]) <= 5 * k)))
        parent[find(i)] = find(j);
  const out = new Map<number, Piece[]>();
  groups.forEach((ps, i) => (out.get(find(i)) ?? out.set(find(i), []).get(find(i))!).push(...ps));
  return [...out.values()];
}
const overlap = (a: number[], b: number[], m: number) => a[0] - m <= b[2] && b[0] - m <= a[2] && a[1] - m <= b[3] && b[1] - m <= a[3];

/**
 * Where a device can be connected: wire ends and corners (two wires meeting at a device pin are joined into one
 * wire with a corner there), once each; not at interruption points and not where a wire ends on another (a T).
 */
export function freeEnds(wires: Wire[], syms: Sym[]): Pt[] {
  const inside = (p: Pt, w: Wire) => w.pts.some((q, i) => i > 0 && segDist(p, [w.pts[i - 1], q]) < 0.01 && Math.hypot(p[0] - q[0], p[1] - q[1]) > 0.01 && Math.hypot(p[0] - w.pts[i - 1][0], p[1] - w.pts[i - 1][1]) > 0.01);
  const out: Pt[] = [], seen = new Set<string>();
  for (const w of wires)
    w.pts.forEach((p, i) => {
      const end = i === 0 || i === w.pts.length - 1;
      if (seen.has(p.join())) return;
      seen.add(p.join());
      if (syms.some((s) => Math.abs(s.x - p[0]) < 0.01 && Math.abs(s.y - p[1]) < 0.01)) return;
      if (end && wires.some((o) => o !== w && inside(p, o))) return;
      out.push(p);
    });
  return out;
}

/** The colours the free wire ends touch most: the colours symbols are drawn in. */
export function symbolColoursOf(pages: { pg: PdfPageData; T: (p: Pt) => Pt; ends: Pt[] }[], wireColour: string): string[] {
  const hits = new Map<string, number>();
  let total = 0;
  for (const { pg, T, ends } of pages) {
    total += ends.length;
    const byColour = new Map<string, Seg[]>();
    for (const p of pg.paths) {
      const c = p.stroke ?? p.fill;
      if (!c || c === wireColour || p.dash) continue;
      const segs = byColour.get(c) ?? byColour.set(c, []).get(c)!;
      for (const poly of p.polys) for (let i = 0; i + 1 < poly.pts.length; i++) segs.push([T(poly.pts[i]), T(poly.pts[i + 1])]);
    }
    for (const [c, segs] of byColour) {
      const n = ends.filter((e) => segs.some((s) => segDist(e, s) < 0.8)).length;
      if (n) hits.set(c, (hits.get(c) ?? 0) + n);
    }
  }
  return [...hits.entries()].filter(([, n]) => n >= Math.max(2, total * 0.05)).sort((a, b) => b[1] - a[1]).map(([c]) => c);
}

/** 32-bit FNV-1a, for symbol signatures. */
function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(36);
}

/** Two drawings match when each line of one lies within 0.6 mm of a line of the other (either direction). */
function sameDrawing(a: Prim[], b: Prim[]): boolean {
  if (a.length !== b.length) return false;
  const left = b.slice();
  const close = (p: [number, number][], q: [number, number][]) => p.length === q.length && p.every((v, i) => Math.abs(v[0] - q[i][0]) <= 0.6 && Math.abs(v[1] - q[i][1]) <= 0.6);
  for (const p of a) {
    const i = left.findIndex((q) =>
      p.k === 'line' && q.k === 'line' ? close(p.pts, q.pts) || close(p.pts, q.pts.slice().reverse())
        : p.k === 'text' && q.k === 'text' ? p.text === q.text && Math.abs(p.x - q.x) <= 0.6 && Math.abs(p.y - q.y) <= 0.6
          : false,
    );
    if (i < 0) return false;
    left.splice(i, 1);
  }
  return true;
}

/** Pairs of connection points a potential passes through: 1–2, 3–4, 13–14, 21–22 … */
function passPairs(labels: string[]): [number, number][] {
  const out: [number, number][] = [];
  labels.forEach((l, i) => {
    const n = Number(l);
    if (!Number.isInteger(n) || n % 2 !== 1) return;
    const j = labels.indexOf(String(n + 1));
    if (j >= 0) out.push([i, j]);
  });
  return out;
}

export interface FnClues {
  role: CustomSymbol['role'];
  kind?: 'no' | 'nc';
  /** connection point names in pin order */
  labels: string[];
  tag: string;
  changeover?: boolean;
  /** a DXF block name the drawing was inserted with ("sigorta", "akım trafosu 2 çekirdekli" …) */
  block?: string;
}

/** Words of a block name → the library symbol (Turkish, English, German; first match wins, the specific ones first). */
const BLOCK_WORDS: [RegExp, string][] = [
  [/motor ?koruma|motorschutz|motor protect/, 'cb3'],
  [/yuk ayirici|load ?break|lasttrenn/, 'lbs3'],
  [/ayirici|disconnector|isolator|trenn/, 'ds3'],
  [/kesici|breaker|leistungsschalter|salter|mccb|acb/, 'mccb3'],
  [/sigorta|fuse|sicherung/, 'fuse'],
  [/parafudr|surge|spd|ueberspannung|uberspannung/, 'spd4'],
  [/akim trafo|current transformer|stromwandler|\bct\b/, 'ct'],
  [/gerilim trafo|voltage transformer|spannungswandler|\bvt\b/, 'vt'],
  [/enerji analiz|power ?meter|energy ?meter|netzanalys|multimet/, 'emeter'],
  [/sayac|kwh|zahler/, 'kwh'],
  [/role.*\b5[01]\b|overcurrent|uberstrom/, 'mrel_i'],
  [/kondansator|capacitor|kondensator/, 'cap3'],
  [/jenerator|generator|generat/, 'gen3'],
  [/kontaktor|contactor|schutz/, 'coil'],
  [/lamba|lamp|leuchte/, 'lamp'],
  [/buton|push ?button|taster/, 'pb_no'],
  [/acil stop|emergency|not ?aus/, 'estop'],
  [/priz|socket|steckdose/, 'socket'],
  [/motor/, 'motor3'],
];
const fold = (s: string) =>
  s.toLocaleLowerCase('tr').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c').replace(/ä/g, 'a').replace(/ß/g, 'ss').replace(/[_\-.]+/g, ' ');

/**
 * The library symbol an imported drawing stands for, from what it is (terminal, contact and its kind), the letter of
 * its tag (IEC 81346-2: K contactor, Q / F protection, M motor, H lamp, S switch …) and its connection point names
 * (A1/A2, U1/V1/W1, X1/X2, 1…6). Undefined where that does not say it clearly.
 */
export function guessFn(c: FnClues): string | undefined {
  const byPins = guessByPins(c);
  if (byPins || !c.block) return byPins;
  // nothing from the connection points: the words of the block name it was inserted with
  const name = fold(c.block);
  return BLOCK_WORDS.find(([re]) => re.test(name))?.[1];
}

function guessByPins(c: FnClues): string | undefined {
  const L = c.labels.map((l) => l.toUpperCase().replace(/\s+/g, ''));
  const has = (...xs: string[]) => xs.every((x) => L.includes(x));
  const letter = /^-?\d*([A-Z]+)/i.exec(c.tag)?.[1].toUpperCase() ?? '';
  if (c.role === 'term') return 'term';
  if (c.changeover) return 'co';
  if (L.length === 2) {
    // coils by their connection points: contactors and relays (often tagged C or R, not only K), valves
    if (has('A1', 'A2')) return ['MB', 'Y', 'YV'].includes(letter) ? 'sol' : !letter || /^[KCR]/.test(letter) ? 'coil' : undefined;
    if (has('D1', 'D2')) return 'coil_uv';
    if (has('C1', 'C2')) return 'coil_st';
    if (has('M1', 'M2')) return 'mot_ch';
    if (c.kind) return letter === 'S' ? (c.kind === 'nc' ? 'pb_nc' : 'pb_no') : c.kind;
    if (has('X1', 'X2')) return letter === 'H' ? 'lamp' : letter === 'HA' || letter === 'KORNA' ? 'horn' : undefined;
    if ((has('+', '-') || has('+', '−')) && /^(G|GB|BAT)$/.test(letter)) return 'battery';
    if (letter === 'F') return has('1', '2') ? 'cb1' : L.every((l) => !l) ? 'fuse' : undefined;
    return undefined;
  }
  if (has('U1', 'V1', 'W1')) return has('U2', 'V2', 'W2') ? 'motor6' : 'motor3';
  if ((has('L', 'N', '+', '-') || has('L', 'N', '+', '−') || has('L', 'N', 'L+', 'M')) && L.length === 4 && ['G', 'T', 'TB'].includes(letter)) return 'psu';
  // three poles numbered 1…6: contactor main contacts (K, C), else a motor protection switch (Q)
  if (has('1', '2', '3', '4', '5', '6') && L.length === 6) return ['K', 'KM', 'C'].includes(letter) ? 'k3' : letter === 'Q' ? 'cb3' : undefined;
  return undefined;
}

/** Adds the devices (and leftover free texts) of one page; returns the new symbols. */
export function importDevices(ctx: DeviceContext): { syms: Sym[]; stats: DeviceStats } {
  const { pg, T, k, symbolColours, wires, used, library } = ctx;
  const stats: DeviceStats = { devices: 0, contacts: 0, terminals: 0, untagged: 0, texts: 0 };
  const out: Sym[] = [], termIds = new Set<string>();
  if (!symbolColours.length) return { syms: out, stats };
  const colours = new Set(symbolColours);

  // symbol graphics as pieces, joined into devices where they touch
  const pieces: Piece[] = [];
  for (const p of pg.paths) {
    const c = p.stroke ?? p.fill;
    if (!c || !colours.has(c) || p.dash) continue;
    for (const poly of p.polys) {
      const pts = poly.pts.map(T);
      let segs: Seg[] = [];
      for (let i = 0; i + 1 < pts.length; i++) {
        if (ctx.skip?.(pts[i], pts[i + 1])) {
          // a wire stroke inside the line: what came before is one piece
          if (segs.length) pieces.push({ segs, bb: bbOf(segs.flat()), dash: false, blk: p.blk });
          segs = [];
        } else segs.push([pts[i], pts[i + 1]]);
      }
      if (segs.length) pieces.push({ segs, bb: bbOf(segs.flat()), dash: false, blk: p.blk });
    }
  }
  const parent = pieces.map((_, i) => i);
  const find = (i: number): number => {
    // iterative with path compression: a long chain in a big drawing must not overflow the stack
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) [parent[i], i] = [r, parent[i]];
    return r;
  };
  const touch = (a: Piece, b: Piece) => a.segs.some(([p, q]) => [p, q].some((e) => b.segs.some((s) => segDist(e, s) < JOIN))) || b.segs.some(([p, q]) => [p, q].some((e) => a.segs.some((s) => segDist(e, s) < JOIN)));
  for (let i = 0; i < pieces.length; i++)
    for (let j = i + 1; j < pieces.length; j++)
      if (find(i) !== find(j) && overlap(pieces[i].bb, pieces[j].bb, JOIN) && touch(pieces[i], pieces[j])) parent[find(i)] = find(j);
  const groups = new Map<number, Piece[]>();
  pieces.forEach((p, i) => (groups.get(find(i)) ?? groups.set(find(i), []).get(find(i))!).push(p));

  let parts = [...groups.values()].flatMap(cells);
  if (ctx.mono) parts = changeovers(parts, k);
  const devs = parts.map((ps) => ({ ps, bb: bbOfPieces(ps), pins: [] as Pt[], through: [] as Seg[] }));

  // in this drawing style the wire runs on through small symbols (fuses, contacts, transformers): the part inside
  // the symbol belongs to its drawing, and where the wire enters and leaves are its connection points
  // (one-colour drawings have their wires told from the drawing already)
  for (const dev of ctx.mono ? [] : devs) {
    const [bx0, by0, bx1, by1] = dev.bb;
    if (bx1 - bx0 > 40 || by1 - by0 > 40) continue;
    for (let wi = 0; wi < wires.length; wi++) {
      const w = wires[wi];
      for (let i = 0; i + 1 < w.pts.length; i++) {
        const a = w.pts[i], b = w.pts[i + 1], h = Math.abs(a[1] - b[1]) < 0.01;
        if (h ? a[1] <= by0 || a[1] >= by1 : a[0] <= bx0 || a[0] >= bx1) continue;
        const lo = Math.max(h ? bx0 : by0, Math.min(h ? a[0] : a[1], h ? b[0] : b[1])), hi = Math.min(h ? bx1 : by1, Math.max(h ? a[0] : a[1], h ? b[0] : b[1]));
        if (hi - lo < 1) continue;
        // cut [lo, hi] out of the segment; pieces shorter than nothing are dropped
        const at = (v: number): Pt => (h ? [snap(v), a[1]] : [a[0], snap(v)]);
        const fwd = (h ? b[0] - a[0] : b[1] - a[1]) > 0, p1 = at(fwd ? lo : hi), p2 = at(fwd ? hi : lo);
        dev.through.push([p1, p2]);
        const before: Pt[] = [...w.pts.slice(0, i + 1), p1], after: Pt[] = [p2, ...w.pts.slice(i + 1)];
        const keep = (pts: Pt[]) => pts.length >= 2 && Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) > 0.01;
        const parts = [before, after].filter(keep).map((pts) => ({ ...w, id: newId(), pts: pts.filter((q, j) => j === 0 || q.join() !== pts[j - 1].join()) }));
        wires.splice(wi, 1, ...parts);
        wi += parts.length - 1;
        break;
      }
    }
  }

  // free wire ends become connection points of the device they touch
  for (const e of freeEnds(wires, ctx.syms)) {
    let best: (typeof devs)[number] | null = null, bd = 0.8;
    for (const d of devs) {
      if (bbDist(e, d.bb) > bd) continue;
      for (const p of d.ps) for (const s of p.segs) {
        const dist = segDist(e, s);
        if (dist < bd) {
          bd = dist;
          best = d;
        }
      }
    }
    // an open switch in an older drawing: its blade starts beside the wire end, which still lies on the symbol's box
    if (ctx.mono) best ??= devs.find((d) => bbDist(e, d.bb) < 0.3 && d.bb[2] - d.bb[0] < 40 && d.bb[3] - d.bb[1] < 40) ?? null;
    best?.pins.push(e);
  }
  // anything up to the page width (a PLC card drawn as a table across the page is one device)
  const wired = devs.filter((d) => d.pins.length && d.pins.length <= 64 && d.bb[2] - d.bb[0] < 430);

  const free = ctx.texts.filter(({ t }) => !used.has(t) && !XREF.test(t.s));
  const small = (t: PdfText) => t.size * k <= 2.8 && t.s.length <= 8;
  // connection point names: the nearest small text to each point
  const labels = wired.map((d) =>
    d.pins.map((p) => {
      let best: PlacedText | null = null, bd = 4;
      for (const x of free) {
        if (used.has(x.t) || !small(x.t)) continue;
        const dist = Math.hypot(x.c[0] - p[0], x.c[1] - p[1]);
        if (dist < bd) {
          bd = dist;
          best = x;
        }
      }
      if (best) used.add(best.t);
      return best?.t.s ?? '';
    }),
  );

  // tags: the nearest tag-like text beside each device, nearest pairs first
  const cands: { d: number; text: PlacedText; score: number }[] = [];
  wired.forEach((dev, d) => {
    for (const x of free) {
      if (used.has(x.t) || x.t.size * k < 2.6 || x.t.s.length > 24 || /^[=+]/.test(x.t.s)) continue;
      const dist = bbDist(x.c, dev.bb);
      if (dist > 12) continue;
      const inside = dist === 0;
      // tags look like "PK5", "F1.1", "-1T1": tag-like words first, with a digit before without
      const word = splitTag(x.t.s)[0];
      cands.push({ d, text: x, score: dist + (inside ? 8 : 0) + (TAGLIKE.test(word) && /[A-ZÇĞİÖŞÜ]/.test(word) ? 0 : 6) + (/\d/.test(word) ? 0 : 3) });
    }
  });
  cands.sort((a, b) => a.score - b.score);
  const tagOf = new Map<number, PlacedText>();
  for (const c of cands) {
    if (tagOf.has(c.d) || used.has(c.text.t)) continue;
    tagOf.set(c.d, c.text);
    used.add(c.text.t);
  }

  wired.forEach((dev, d) => {
    const tagText = tagOf.get(d);
    const [word, rest] = splitTag(tagText?.t.s ?? '');
    let tag = word.replace(/^-/, '').slice(0, 40);
    // description: written after the tag, or right under it (e.g. "C-1P6A", "3x1000A")
    let desc = rest.slice(0, 200);
    if (tagText && !desc) {
      const under = free.find(({ t, c }) => !used.has(t) && Math.abs(c[0] - tagText.c[0]) < 8 && c[1] > tagText.c[1] && c[1] - tagText.c[1] < 5);
      if (under) {
        used.add(under.t);
        desc = under.t.s.slice(0, 200);
      }
    }
    // origin at the first connection point (top, then left): pins stay exactly on the wire ends, and
    // the same drawing gives the same symbol wherever it stands
    const order = dev.pins.map((_, i) => i).sort((a, b) => dev.pins[a][1] - dev.pins[b][1] || dev.pins[a][0] - dev.pins[b][0]);
    dev.pins = order.map((i) => dev.pins[i]);
    const lab = order.map((i) => labels[d][i]);
    const [ox, oy] = dev.pins[0];
    // a terminal is a small ring on the wire (with perhaps a short connecting stroke)
    const ring = dev.ps.some((p) => ringOf(p) > 0 && ringOf(p) <= 2.6);
    // or, in older drawings, a larger circle in its own square, nothing drawn inside the circle (a lamp has a cross)
    const big = dev.ps.find((p) => ringOf(p) > 2.6 && ringOf(p) <= 8);
    const cell = !!big && dev.ps.every((p) => p === big || !(p.bb[0] > big.bb[0] && p.bb[2] < big.bb[2] && p.bb[1] > big.bb[1] && p.bb[3] < big.bb[3])) && dev.bb[2] - dev.bb[0] <= ringOf(big) * 1.5 && dev.bb[3] - dev.bb[1] <= ringOf(big) * 1.5;
    const term = ((ring && dev.bb[2] - dev.bb[0] <= 5 && dev.bb[3] - dev.bb[1] <= 5) || cell) && dev.pins.length <= 2;
    const coil = lab.includes('A1') && lab.includes('A2');
    const contact = !term && dev.pins.length === 2 && /^\d\d$/.test(lab[0]) && /^\d\d$/.test(lab[1]) && lab[0][0] === lab[1][0];
    // a changeover contact: x1 common, x2 opening, x4 closing
    const changeover = !term && dev.pins.length === 3 && lab.every((l) => /^\d\d$/.test(l) && l[0] === lab[0][0]) && lab.map((l) => l[1]).sort().join('') === '124';
    let kind: 'no' | 'nc' | undefined = contact ? (['1', '2'].includes(lab[0][1]) && ['1', '2'].includes(lab[1][1]) ? 'nc' : 'no') : changeover ? 'no' : undefined;
    let role: CustomSymbol['role'] = term ? 'term' : (contact || changeover) && !coil ? 'aux' : 'main';
    // the drawing, relative to the device origin; texts inside the device (black box functions) go with it
    const prims: Prim[] = [];
    for (const p of [...dev.ps].sort((a, b) => b.segs.length - a.segs.length)) {
      const pts: [number, number][] = [p.segs[0][0], ...p.segs.map((s) => s[1])].slice(0, 200).map((q) => [r2(q[0] - ox), r2(q[1] - oy)]);
      if (prims.length < 480) prims.push({ k: 'line', pts });
    }
    // the wire inside the symbol is now part of its drawing
    for (const [p1, p2] of dev.through) prims.push({ k: 'line', pts: [[r2(p1[0] - ox), r2(p1[1] - oy)], [r2(p2[0] - ox), r2(p2[1] - oy)]] });
    for (const x of free)
      if (!used.has(x.t) && bbDist(x.c, dev.bb) === 0 && Math.abs(x.t.angle) < 1 && prims.length < 500) {
        used.add(x.t);
        prims.push({ k: 'text', x: r2(x.c[0] - (x.t.w * k) / 2 - ox), y: r2(x.c[1] + x.t.size * k * 0.35 - oy), size: r2(Math.min(20, Math.max(1, x.t.size * k))), text: x.t.s.replace(/[<>&"]/g, '').slice(0, 40) });
      }
    const pins: CustomPin[] = dev.pins.map((p, i) => ({ x: r2(p[0] - ox), y: r2(p[1] - oy), label: term ? '' : lab[i].replace(/[<>&"]/g, '').slice(0, 12) }));
    // potentials pass through contacts, terminals and switching or protective devices (F, Q, multi-pole), not through
    // loads such as a horn numbered 1–2; where the wire ran through such a symbol, it passes there too
    // multi-pole: 4 to 8 points numbered 1…8 on a small symbol (black boxes number theirs 1…40 and must not pass)
    const multiPole = dev.pins.length >= 4 && dev.pins.length <= 8 && lab.every((l) => /^[1-8]$/.test(l)) && dev.bb[2] - dev.bb[0] < 40 && dev.bb[3] - dev.bb[1] < 40;
    const switching = /^-?\d*[FQ]/i.test(tag) || multiPole;
    // a fuse or breaker with two unnumbered connection points passes between them
    const pass: [number, number][] = term || (contact && dev.pins.length === 2) || (switching && dev.pins.length === 2 && !lab.some(Boolean)) ? [[0, 1]] : switching ? passPairs(lab) : [];
    if (changeover) {
      const at = (d: string) => lab.findIndex((l) => l[1] === d);
      pass.push([at('1'), at('2')], [at('1'), at('4')]);
    }
    if (!coil && (switching || contact))
      for (const [p1, p2] of dev.through) {
        const i = dev.pins.findIndex((q) => q.join() === p1.join()), j = dev.pins.findIndex((q) => q.join() === p2.join());
        if (i >= 0 && j >= 0 && !pass.some(([x, y]) => (x === i && y === j) || (x === j && y === i))) pass.push([i, j]);
      }
    const letters = (tag.normalize('NFD').replace(/[^\x20-\x7e]/g, '').toUpperCase().match(/[A-Z]+/)?.[0] ?? 'U').slice(0, 4);
    // the library symbol it stands for (part lists, checks, panel layout and 3D go by that)
    const blocks = new Set(dev.ps.map((p) => p.blk ?? ''));
    const fn = guessFn({ role, kind, labels: lab, tag, changeover, ...(blocks.size === 1 && [...blocks][0] ? { block: [...blocks][0] } : {}) });
    // a contactor's main contacts are contacts of its coil, not a second device
    if (fn === 'k3') [role, kind] = ['aux', 'no'];
    // copies of the same drawing share one symbol: same connection points and texts, lines within 0.6 mm
    const shape = hash(JSON.stringify({ role, kind, fn, pins, pass, n: prims.map((p) => (p.k === 'line' ? p.pts.length : p.k === 'text' ? 't' + p.text : p.k)).sort() }));
    let sym: CustomSymbol | undefined, n = 0;
    for (; library.has(`${shape}.${n}`); n++)
      if (sameDrawing(library.get(`${shape}.${n}`)!.prims, prims)) {
        sym = library.get(`${shape}.${n}`);
        break;
      }
    const sig = `${shape}.${n}`;
    if (!sym) {
      // the key comes from the drawing itself (two hashes): projects share one symbol registry, so the same key
      // must always mean the same drawing
      const content = JSON.stringify({ role, kind, fn, pins, pass, prims });
      sym = {
        key: `u_eplan_${hash(content)}${hash(content + '|')}`, name: `EPLAN ${letters} ${term ? '' : lab.filter(Boolean).join('/')}`.trim().slice(0, 60), cat: 'EPLAN', prefix: letters,
        role, ...(kind ? { kind } : {}), ...(fn ? { fn } : {}), mirror: false, pins, pass, prims,
      };
      library.set(sig, sym);
    }
    const props: Sym['props'] = {};
    let tno = lab.find(Boolean);
    // "1X21": terminal 21 of strip 1X, written as one name
    const joined = /^(\d+X)(\d+)$/i.exec(tag);
    if (term && !tno && joined) [tag, tno] = [joined[1], joined[2]];
    if (term && tno) props.pinNo = tno;
    const s: Sym = { id: newId(), type: sym.key, x: ox, y: oy, rot: 0, tag, desc, part: '', maker: '', props };
    out.push(s);
    if (term) termIds.add(s.id);
    stats.devices++;
    if (role === 'aux') stats.contacts++;
    if (term) stats.terminals++;
    if (!tag && !term) stats.untagged++;
  });

  // terminals show the strip name once; the others take it from the terminal above or to the left
  const terms = out.filter((s) => termIds.has(s.id));
  for (const s of terms.filter((x) => !x.tag)) {
    const from = terms
      .filter((o) => o.tag && ((Math.abs(o.x - s.x) < 1 && o.y < s.y) || (Math.abs(o.y - s.y) < 1 && o.x < s.x)))
      .sort((a, b) => Math.hypot(a.x - s.x, a.y - s.y) - Math.hypot(b.x - s.x, b.y - s.y))[0];
    if (from) s.tag = from.tag;
  }

  // the rest of the horizontal and vertical texts inside the drawing area stay as free texts
  const [ax0, ay0, ax1, ay1] = ctx.area;
  for (const x of free) {
    const quarter = Math.round(x.t.angle / 90), rot = ((quarter % 4) + 4) % 4;
    if (used.has(x.t) || Math.abs(x.t.angle - quarter * 90) > 1 || x.t.size * k < 2.4 || x.t.bold) continue;
    if (x.c[0] < ax0 || x.c[0] > ax1 || x.c[1] < ay0 || x.c[1] > ay1) continue;
    used.add(x.t);
    // from the middle back to the start of the baseline: half the width against the reading direction, down to the baseline
    const dir = rotP(1, 0, rot), down = rotP(0, 1, rot), half = (x.t.w * k) / 2, drop = x.t.size * k * 0.35;
    const at: Pt = [x.c[0] - dir[0] * half + down[0] * drop, x.c[1] - dir[1] * half + down[1] * drop];
    out.push({ id: newId(), type: 'text', x: r2(at[0]), y: r2(at[1]), rot, tag: '', desc: '', part: '', maker: '', props: { text: x.t.s.slice(0, 200), size: r2(x.t.size * k) } });
    stats.texts++;
  }
  return { syms: out, stats };
}
